<script setup lang="ts">
import Isologo from '~/components/logos/IsoLogo.vue'
import { useResultStore } from '~/store/result'
import { useSubscriberStore } from '~/store/subscriber'
import { getOnlyNumbers } from '~/utils/numbers'
import { z } from 'zod'
import { useMediaQuery } from '@vueuse/core'
import { trackAlertSaved } from '~/tracking/resultEvents'
import { ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries'

defineOptions({
	name: 'ModalContact',
})

const emit = defineEmits(['update:modelValue', 'updateStatusForm'])

type SubscribeBody = {
	name: string
	email: string
	lottery_id?: number
	country_id?: number
	numbers?: number[]
	lang?: string
}

interface Props {
	modelValue?: boolean
	preventClose?: boolean
	isUserRegister?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: false,
	preventClose: true,
	isUserRegister: false,
})

const cdn = useRuntimeConfig().public.cdn || ''
const jsonUrl = `${cdn}/animations-lottie/check.json`
const lottieContainer = ref<HTMLDivElement | null>(null)
const initialFocusEl = ref<HTMLElement | null>(null)
const nameWrapperRef = ref<HTMLElement | null>(null) // wrapper por si acaso
const isLoading = ref(false)
const isCompletedForm = ref(false)

const isDesktop = useMediaQuery('(min-width: 640px)')

const { t: $t, locale } = useI18n()
const localePath = useLocalePath()
const subscriberStore = useSubscriberStore()
const resultStore = useResultStore() as {
	user: { email: string; name: string } | null
	lottery: { name?: string; id?: number } | null
	country: { id?: number } | null
	current_numbers: any
	isCompletedActiveAlarms: boolean
	SET_STATE_RESULT: (payload: any) => void
}

const labelClass = {
	base: 'text-base text-neutral-900 font-semibold leading-6',
	required: `after:content-['*'] after:ms-0.5 after:text-destructive-600`,
}
const inputUi = {
	wrapper: 'input-class',
	placeholder: 'placeholder-neutral-500 placeholder:text-sm',
}
const inputClass = 'h-[42px] px-3 py-2.5 text-neutral-800 text-base'

const onlyLetters = /[a-zA-Z]+/
const schema = z.object({
	name: z
		.string()
		.min(3, `${$t('Must be at least 3 characters')}.`)
		.regex(onlyLetters, `${$t('Only letters')}.`),
	email: z.string().email(`${$t('Invalid email')}.`),
	agreeToTerms: z.boolean().refine((val) => val === true, {
		message: `${$t('You must agree to the terms and conditions')}.`,
	}),
})

const state = reactive({
	name: '',
	email: '',
	agreeToTerms: false,
})

const isDisabledBtn = computed(() => !schema.safeParse(state).success)

const setUser = (user: any) => {
	const value = user === null ? state : user
	const userData = { email: value.email, name: value.name }
	localStorage.setItem('user', JSON.stringify(userData))
	resultStore.SET_STATE_RESULT({
		type: 'user',
		value: { ...userData },
	})
	return
}

const setEvent = () => {
	const params = {
		lottery_id: resultStore.lottery?.id,
		game_name: resultStore.lottery?.name,
		user_name: state.name,
		email: state.email,
	}
	trackAlertSaved(params)
}

const verificatedAlertActive = async () => {
	const validateContent = {
		email: state.email,
		lottery_id: resultStore.lottery?.id,
		country_id: resultStore.country?.id,
	}
	const response = await subscriberStore.validateSubscriber(validateContent)
	if (state.email !== '' && state.name !== '' && resultStore.user === null) {
		setUser(state)
	}

	resultStore.SET_STATE_RESULT({
		type: 'isCompletedActiveAlarms',
		value: response.success === true,
	})
	isCompletedForm.value = response.success === true
	return response.success
}

const onSubmit = async (event: any) => {
	event.preventDefault()
	isLoading.value = true

	const validate = await verificatedAlertActive()

	if (validate === true) {
		isLoading.value = false
		return
	}

	const subscribeBody: SubscribeBody = {
		name: state.name,
		email: state.email,
		lottery_id: resultStore.lottery?.id,
		country_id: resultStore.country?.id,
		lang: locale.value,
	}
	if (resultStore.current_numbers !== null) {
		const currentNumbers = getOnlyNumbers(resultStore.current_numbers)
		subscribeBody.numbers = currentNumbers
	}

	const response = await subscriberStore.saveSubscriber(subscribeBody)

	isLoading.value = false

	if (!response.success) return
	isCompletedForm.value = true
	setEvent()

	if (resultStore.user === null) setUser(state)
}

const isOpen = computed({
	get() {
		return props.modelValue
	},
	set(value) {
		emit('update:modelValue', value)
	},
})

const setLottieComponent = () => {
	if (!lottieContainer.value) return
	const player = document.createElement('lottie-player')
	player.setAttribute('background', 'transparent')
	player.setAttribute('speed', '1')
	player.setAttribute('loop', '')
	player.setAttribute('autoplay', '')
	player.style.width = '100%'
	player.style.height = '100%'

	player.setAttribute('src', jsonUrl)

	lottieContainer.value.appendChild(player)
}

const handleAfterLeave = async () => {
	verificatedAlertActive()
	await resultStore.SET_STATE_RESULT({
		type: 'isCompletedActiveAlarms',
		value: isCompletedForm.value,
	})
	isCompletedForm.value = false
}

const setStateUser = () => {
	const { name, email } = resultStore?.user || {}
	state.name = name || ''
	state.email = email || ''
}

const focusNameInput = () => {
	if (!isDesktop.value) return

	nextTick(() => {
		requestAnimationFrame(() => {
			nameWrapperRef.value?.querySelector('input')?.focus()
		})
	})
}

const resetIOSZoom = () => {
	if (typeof window === 'undefined') return

	const viewport = document.querySelector('meta[name=viewport]')
	if (!viewport) return

	viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1')
}

watch(
	() => isCompletedForm.value,
	(value) => {
		if (value) {
			nextTick(() => {
				setLottieComponent()
			})
		}
	},
	{ immediate: true },
)

watch(
	[() => isOpen.value, () => isCompletedForm.value],
	([open, done], [prevOpen]) => {
		/* (solo desktop) */
		if (open && !done && isDesktop.value) {
			focusNameInput()
		}

		// Nuevo: al cerrar (transición open->closed)
		if (prevOpen && !open) {
			nextTick(() => {
				resetIOSZoom()
			})
		}
	},
	{ flush: 'post' },
)

onMounted(() => {
	if (resultStore.user === null) return
	setStateUser()
})
</script>

<template>
	<ClientOnly>
		<UModal
			v-model="isOpen"
			:prevent-close="props.preventClose"
			:initial-focus="isDesktop ? null : initialFocusEl"
			class="modal-contact"
			:ui="{
				overlay: {
					background: 'bg-[#040404D4] bg-opacity-80',
				},
				width: 'w-full sm:max-w-[592px]',
				container: 'items-center',
				padding: 'p-8 sm:p-0',
			}"
			@after-leave="handleAfterLeave"
		>
			<span
				ref="initialFocusEl"
				tabindex="-1"
				class="sr-only"
			/>
			<UCard
				:ui="{
					ring: '',
					divide: 'divide-y divide-gray-100 dark:divide-gray-800',
					body: { padding: 'p-8' },
				}"
			>
				<div
					v-if="isUserRegister"
					class="sm:divide-y sm:divide-[#E1E1E6] space-y-8 pt-8"
				>
					<div class="space-y-5">
						<div class="relative flex justify-center">
							<div
								class="size-16 rounded-full border-8 border-primary-500 bg-white absolute -top-3 z-10 flex justify-center items-center"
							>
								<UIcon
									name="i-ph:check-fat-fill"
									class="size-7 text-primary-500"
								/>
							</div>
							<UIcon
								name="i-heroicons:envelope"
								class="size-36 text-primary-500"
							/>
						</div>
						<div class="space-y-4 pt-8 sm:pt-0 text-center">
							<h2 class="text-primary-500 font-semibold text-xl sm:text-3xl">{{ $t('Alert Already Active') }}</h2>
							<p class="text-base text-neutral-800 leading-6 sm:px-10">
								{{
									$t(
										`Your lottery alert is active! You’ll receive an email with the latest {lottery} winning numbers after each weekly draw`,
										{
											lottery:
												resultStore?.lottery?.id === ID_EURO_MILLIONS
													? $t(resultStore.lottery?.name ?? '')
													: (resultStore.lottery?.name ?? ''),
										},
									)
								}}.
							</p>
						</div>
					</div>
					<div class="flex justify-end sm:pt-8">
						<UButton
							:label="$t('Close')"
							color="primary"
							:ui="{
								rounded: 'rounded-full',
							}"
							class="px-5 w-full sm:w-auto flex justify-center"
							@click="isOpen = false"
						/>
					</div>
				</div>
				<template v-else>
					<div
						v-if="isCompletedForm"
						class="sm:divide-y sm:divide-[#E1E1E6] space-y-8"
					>
						<div class="text-center space-y-4 divide-y sm:divide-y-0 :divide-[#E1E1E6]">
							<div class="max-w-32 smm:max-w-40 mx-auto">
								<div ref="lottieContainer" />
							</div>
							<div class="space-y-4 pt-8 sm:pt-0">
								<h2 class="text-primary-500 font-semibold text-xl sm:text-3xl">
									{{ $t('Success! Your Alerts Are ON') }}
								</h2>
								<div
									v-if="resultStore.user && resultStore.lottery"
									class="space-y-10 text-neutral-800 leading-6 max-w-[440px] mx-auto"
								>
									<p>
										{{
											$t(
												`We’ve set up your free lottery alerts! We’ll send a notification to {email} with the latest {lottery} winning numbers after each weekly draw`,
												{
													email: resultStore.user.email,
													lottery:
														resultStore?.lottery?.id === ID_EURO_MILLIONS
															? $t(resultStore.lottery?.name ?? '')
															: (resultStore.lottery?.name ?? ''),
												},
											)
										}}.
									</p>
									<p>{{ $t('Remember to check your spam folder occasionally') }}.</p>
								</div>
							</div>
						</div>
						<div class="flex justify-end sm:pt-8">
							<UButton
								:label="$t('Close')"
								color="primary"
								:ui="{
									rounded: 'rounded-full',
								}"
								class="px-5 w-full sm:w-auto flex justify-center"
								@click="isOpen = false"
							/>
						</div>
					</div>
					<div
						v-else
						class="divide-y divide-[#E1E1E6] space-y-8"
					>
						<div class="space-y-5 text-center sm:text-left">
							<Isologo
								class="text-primary-600 mx-auto sm:mx-0"
								:size="40"
							/>
							<div class="flex flex-col gap-4">
								<h2 class="text-primary-500 font-semibold text-xl sm:text-3xl">
									{{ $t('Get notifications for the upcoming winning numbers') }}.
								</h2>
								<slot />
							</div>
						</div>
						<div class="pt-8">
							<UForm
								ref="form"
								:schema="schema"
								:state="state"
								class="space-y-4"
								@submit="onSubmit"
							>
								<UFormGroup
									:label="$t('Your full name')"
									name="name"
									required
									:ui="{
										label: labelClass,
									}"
								>
									<UInput
										v-model="state.name"
										placeholder="Jane Doe"
										variant="outline"
										:ui="inputUi"
										:input-class="inputClass"
										:autofocus="false"
									/>
								</UFormGroup>
								<div class="space-y-1">
									<UFormGroup
										:label="$t('Enter your email address')"
										name="email"
										required
										:ui="{
											label: labelClass,
										}"
									>
										<UInput
											v-model="state.email"
											type="email"
											placeholder="youremail@email.com"
											:ui="inputUi"
											:input-class="inputClass"
										/>
									</UFormGroup>
									<p class="text-xs text-neutral-800 leading-5">{{ $t('Easily unsubscribe whenever you wish') }}.</p>

									<!-- Terms Checkbox -->
									<UCheckbox
										v-model="state.agreeToTerms"
										class="pt-4 smm:pt-6"
										:ui="{
											error: 'text-destructive-800',
										}"
									>
										<template #label>
											<p class="text-sm text-neutral-800">
												{{ $t('I agree to receive email notifications and accept the') }}

												<NuxtLink
													:to="localePath('/privacy-policy')"
													class="text-primary-600 hover:text-opacity-80"
													target="blank"
													>{{ $t('Privacy Policy') }}</NuxtLink
												>
											</p>
										</template>
									</UCheckbox>
								</div>

								<div class="flex justify-end pt-4">
									<UButton
										type="submit"
										:label="$t('Activate Alerts')"
										:loading="isLoading"
										:disabled="isDisabledBtn"
										color="white"
										:ui="{
											rounded: 'rounded-full',
											color: 'white',
										}"
										class="px-5 py-2 text-base font-semibold w-full sm:w-auto flex justify-center"
										:class="
											isDisabledBtn === true || isLoading === true
												? 'border border-neutral-400 bg-neutral-500 text-white'
												: 'hover:bg-opacity-70 bg-primary-500 border-none text-white'
										"
									>
										<template #trailing>
											<UIcon
												name="i-heroicons-arrow-right-20-solid"
												class="w-6 h-6"
											/>
										</template>
									</UButton>
								</div>
							</UForm>
						</div>
					</div>
				</template>
			</UCard>
		</UModal>
	</ClientOnly>
</template>

<style>
.modal-contact {
	.input-class {
		box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
	}
}
</style>
